import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings as SettingsIcon, LogOut, Home, Menu, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Link2, ExternalLink, Truck, BarChart2, Package, PackageCheck, ClipboardList, FileText, TrendingUp, Send } from 'lucide-react';
import DashboardPanel from './DashboardPanel';
import GlobalSearch from './GlobalSearch';
import GlobalNotificationBar from './GlobalNotificationBar';
import M45NavPills from './M45NavPills';
import OrderBookAnswerModal from './OrderBookAnswerModal';
import { useBranch } from '../context/BranchContext';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import ActivityBar from './ActivityBar';
import HubLightBackground from './HubLightBackground';
import LightPageBackground from './LightPageBackground';
import { VIEWPORT_PROBE_STYLE, MODAL_VIEWPORT_CSS, computeModalViewport, applyModalViewportVars, watchModalPanels } from '../utils/modalViewport';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import api from '../services/api';
import { API } from '../App';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useLimitedShell } from '../limited/LimitedShellContext';
import LimitedTopBar from '../limited/LimitedTopBar';
import V3PageToolbar from '../v3/V3PageToolbar';
import { useV3Shell } from '../v3/V3ShellContext';

const enconOrange = '#ee7203';
const enconBlack = '#1d1d1b';
const pastelOrange = '#fbbf80'; // softer, pastel version of the brand orange
const subHeaderOrangeGradient = 'linear-gradient(90deg, #ee7203 0%, rgba(238,114,3,0) 32%, rgba(238,114,3,0) 68%, #ee7203 100%)';
// Sub-header style alternatives to the default orange/white/orange gradient above —
// selectable per-user (Settings > General > My Personal Style) or org-wide
// (Settings > General > Default Style For All Users, super_admin only).
const subHeaderDarkGradient = 'linear-gradient(90deg, #ee7203 0%, #1d1d1b 34%, #1d1d1b 66%, #ee7203 100%)';
const subHeaderOrangeBarGradient = 'linear-gradient(90deg, #ee7203 0%, #fbbf80 34%, #fbbf80 66%, #ee7203 100%)';
const subHeaderOrangeWhiteGradient = 'linear-gradient(90deg, #ee7203 0%, #ffffff 34%, #ffffff 66%, #ee7203 100%)';
// Global Header (main white nav row, ABOVE the sub-header) dark style — reuses the same
// near-black palette as the Home page's own dark background for visual consistency.
const headerDarkGradient = 'linear-gradient(90deg, #0a0a0a 0%, #232323 50%, #0a0a0a 100%)';
// Global Header dotted-graphite style — the fine dot grid that used to sit at the top of the
// Home page's dark background, now living in the header on a graphite gradient. Only painted when
// the user's Home theme is dark AND the 'global-header-dots-background' Manage Changes toggle is
// switched on for them (its own toggle, independent of the Home page background change).
// Tuned so the header stays clearly DARKER than the page background behind it (graphite #474a53 at the top of the
// page) but not so dark that the dots disappear: a mid-graphite base, and dots bright enough to read on it.
const headerDotsGradient = 'radial-gradient(circle, rgba(255,255,255,0.22) 1.1px, rgba(255,255,255,0) 1.8px) 0 0 / 20px 20px, linear-gradient(90deg, #3a3c44 0%, #2a2c33 50%, #3a3c44 100%)';
const HEADER_DOTS_FLAG = 'global-header-dots-background';
// The white counterpart: the same fine dot grid, in a faint grey on a white-to-silver gradient. Used in place
// of the plain white header for people on the LIGHT Home theme, under the same 'global-header-dots-background'
// toggle. It never overrides a user's own dark header choice.
const headerDotsLightGradient = 'radial-gradient(circle, rgba(29,29,27,0.11) 1px, rgba(29,29,27,0) 1.6px) 0 0 / 20px 20px, linear-gradient(90deg, #ffffff 0%, #f3f4f7 50%, #ffffff 100%)';

// ──────────────────────────────────────────────────────────────────────────────────────────────
// Glass styling for every button in the Global Header and the Global Sub-Header, on every page.
//
// Same look as the Home page cards: frosted glass with a soft inner highlight and depth. The
// colour treatment follows each row's own background — dark glass on dark rows (dotted / dark
// header, dark Home screen, Transport), light frosted glass on light rows — so it reads as one
// system whichever page you are on. It is applied by a `data-c30-glass="dark|light"` attribute on
// the nav row and the sub-header row (set below from the page's colour arrangement), and it only
// targets the header controls themselves, never the dropdown menus or modals they open.
//
// Sizes: the Transport icon (also while it is the "back to <last page>" button), the Call icon and
// the hamburger / settings / home icons all match the height of the pills. The pills get their height
// from Tailwind's h-9 (2.25rem), so these use the same rem value rather than a fixed pixel size —
// that way they stay level with the pills at any root font size / screen.
// ──────────────────────────────────────────────────────────────────────────────────────
const NAV_ROW = '[data-testid="global-header-nav-row"][data-c30-glass]';
const NAV_ROW_DARK = '[data-testid="global-header-nav-row"][data-c30-glass="dark"]';
const SUB_ROW = '[data-testid="global-sub-header"][data-c30-glass]';
const SUB_ROW_DARK = '[data-testid="global-sub-header"][data-c30-glass="dark"]';
const SUB_ROW_LIGHT = '[data-testid="global-sub-header"][data-c30-glass="light"]';
const tid = (id) => `[data-testid="${id}"]`;

const GLASS_NAV_PILLS = [
  tid('nav-pill-proforma-dropdown'),
  tid('nav-pill-quotes-dropdown'),
  tid('log-call-dropdown-pill'),
  tid('log-call-dropdown-pill-disabled'),
  tid('nav-pill-stock-transfers-requests'),
  tid('nav-pill-stock-management'),
  tid('nav-pill-po-expenses'),
  tid('nav-pill-admin-dropdown'),
  '[data-testid^="nav-header-custom-link-"]',
  '[data-testid^="nav-header-page-link-"]',
  '[data-testid^="nav-header-custom-dropdown-"]:not([data-testid*="-child-"])',
];
const GLASS_NAV_ICON_BUTTONS = [
  tid('header-delivery-icon-btn'),
  tid('header-transport-icon-btn'),
  tid('header-quick-call-note-btn'),
];
const GLASS_NAV_SQUARE_BUTTONS = [
  tid('header-sidebar-toggle-btn'),
  tid('header-settings-menu-btn'),
  tid('header-home-btn'),
];
const GLASS_NAV_TINTED = [tid('nav-header-sector-dropdown-trigger'), tid('header-send-answers-btn')];
const GLASS_SUB_TOGGLES = [tid('global-search-customer-toggle'), tid('global-search-project-toggle')];
const GLASS_SUB_SEARCH_BAR = '[data-search-trigger] > [data-search-trigger]';
const GLASS_NAV_SLOT_ITEMS = ['.c30-nav-slot > .bg-white'];
const GLASS_SUB_SLOT_ITEMS = ['.c30-sub-slot > .bg-white'];

const glassWithin = (row, list, suffix = '') => list.map((s) => `${row} ${s}${suffix}`).join(',\n');
const glassInside = (row, list, kids) => list.flatMap((s) => kids.map((k) => `${row} ${s} ${k}`)).join(',\n');
const GLASS_NAV_PILLS_NO_LOGCALL = GLASS_NAV_PILLS.filter((s) => !s.includes('log-call'));

const HEADER_GLASS_CSS = `
/* ── Glass tokens ─────────────────────────────────────────────────────────────────────────────── */
[data-c30-glass="light"] {
  --g-bg: linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(255,246,238,0.70) 55%, rgba(255,255,255,0.86) 100%);
  --g-bg-hover: linear-gradient(160deg, #ffffff 0%, rgba(255,238,224,0.92) 55%, #ffffff 100%);
  --g-border: 1px solid rgba(238,114,3,0.30);
  --g-shadow: inset 0 1.5px 0 rgba(255,255,255,0.95), inset 0 -1px 0 rgba(238,114,3,0.12), 0 4px 14px rgba(29,29,27,0.10), 0 1px 3px rgba(29,29,27,0.08);
  --g-shadow-hover: inset 0 1.5px 0 #ffffff, inset 0 -1px 0 rgba(238,114,3,0.16), 0 8px 22px rgba(238,114,3,0.24), 0 2px 6px rgba(29,29,27,0.10);
  --g-blur: blur(16px) saturate(1.6);
  --g-accent: #ee7203;
  --g-accent-teal: #0d9488;
}
[data-c30-glass="dark"] {
  --g-bg: linear-gradient(160deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.07) 100%);
  --g-bg-hover: linear-gradient(160deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.12) 100%);
  --g-border: 1px solid rgba(255,255,255,0.18);
  --g-shadow: inset 0 1.5px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.15), 0 6px 16px rgba(0,0,0,0.35), 0 2px 5px rgba(0,0,0,0.20);
  --g-shadow-hover: inset 0 1.5px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.15), 0 10px 26px rgba(0,0,0,0.50), 0 0 18px rgba(255,106,0,0.35);
  --g-blur: blur(20px) saturate(1.5);
  --g-accent: #ff9838;
  --g-accent-teal: #2dd4bf;
}
/* The sub-header sits on the orange gradient at its edges, so its glass is tinted to stay legible. */
${SUB_ROW_DARK} {
  --g-bg: linear-gradient(160deg, rgba(255,255,255,0.16) 0%, rgba(0,0,0,0.14) 55%, rgba(0,0,0,0.26) 100%);
  --g-bg-hover: linear-gradient(160deg, rgba(255,255,255,0.26) 0%, rgba(0,0,0,0.10) 55%, rgba(0,0,0,0.22) 100%);
}
/* The dark sub-header style keeps the same transparent bar as the default one — only the buttons go
   dark. When it is chosen on a LIGHT page the light page shows through the bar, so that dark glass has
   to be denser than on a dark page or the white text would wash out. */
${SUB_ROW_DARK}[data-c30-glass-solid] {
  --g-bg: linear-gradient(160deg, rgba(64,66,75,0.88) 0%, rgba(30,31,36,0.92) 100%);
  --g-bg-hover: linear-gradient(160deg, rgba(80,82,92,0.94) 0%, rgba(40,41,47,0.96) 100%);
}
${SUB_ROW_LIGHT} {
  --g-bg: #ffffff;
  --g-bg-hover: #ffffff;
  --g-border: 1px solid rgba(0,0,0,0.08);
}

/* ── Glass surface ─────────────────────────────────────────────────────────────────────────────── */
${glassWithin(NAV_ROW, [...GLASS_NAV_PILLS, ...GLASS_NAV_ICON_BUTTONS, ...GLASS_NAV_SQUARE_BUTTONS, ...GLASS_NAV_SLOT_ITEMS])},
${glassWithin(SUB_ROW, [...GLASS_SUB_TOGGLES, ...GLASS_SUB_SLOT_ITEMS])},
${SUB_ROW} ${GLASS_SUB_SEARCH_BAR} {
  background: var(--g-bg) !important;
  border: var(--g-border) !important;
  box-shadow: var(--g-shadow) !important;
  -webkit-backdrop-filter: var(--g-blur);
  backdrop-filter: var(--g-blur);
}
${glassWithin(NAV_ROW, [...GLASS_NAV_PILLS, ...GLASS_NAV_ICON_BUTTONS, ...GLASS_NAV_SQUARE_BUTTONS], ':hover')},
${glassWithin(NAV_ROW, ['.c30-nav-slot > button.bg-white', '.c30-nav-slot > a.bg-white'], ':hover')},
${glassWithin(SUB_ROW, [...GLASS_SUB_TOGGLES, '.c30-sub-slot > button.bg-white', '.c30-sub-slot > a.bg-white'], ':hover')} {
  background: var(--g-bg-hover) !important;
  box-shadow: var(--g-shadow-hover) !important;
}
${SUB_ROW} ${GLASS_SUB_SEARCH_BAR}.ring-2 {
  box-shadow: var(--g-shadow), 0 0 0 2px rgba(255,152,56,0.75) !important;
}
${NAV_ROW} ${tid('header-sidebar-toggle-btn')}.bg-orange-100 {
  background: linear-gradient(160deg, rgba(238,114,3,0.30) 0%, rgba(238,114,3,0.14) 100%) !important;
}

/* ── Icons + text follow the theme's accent colour ────────────────────────────────────────────────────────────────── */
${glassInside(NAV_ROW, GLASS_NAV_PILLS_NO_LOGCALL, ['span', 'svg'])},
${glassInside(NAV_ROW, [...GLASS_NAV_ICON_BUTTONS, ...GLASS_NAV_SQUARE_BUTTONS], ['svg'])} {
  color: var(--g-accent) !important;
}
${NAV_ROW_DARK} ${tid('log-call-dropdown-pill')} span {
  background-image: linear-gradient(135deg, #bfdbfe 0%, #60a5fa 100%) !important;
}
${NAV_ROW_DARK} ${tid('log-call-dropdown-pill')} svg {
  color: #93c5fd !important;
}

/* ── Transport (also the "back to <last page>" state), Call and the small icon buttons: same height as the pills (h-9 = 2.25rem) ── */
${glassWithin(NAV_ROW, GLASS_NAV_ICON_BUTTONS)} {
  height: 2.25rem !important;
  min-width: 2.75rem;
  padding: 0 0.75rem !important;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem !important;
}
${glassInside(NAV_ROW, GLASS_NAV_ICON_BUTTONS, ['svg'])} {
  width: 1.25rem !important;
  height: 1.25rem !important;
}
${glassInside(NAV_ROW, GLASS_NAV_ICON_BUTTONS, ['svg + svg'])} {
  width: 0.875rem !important;
  height: 0.875rem !important;
}
${glassWithin(NAV_ROW, GLASS_NAV_SQUARE_BUTTONS)} {
  width: 2.25rem !important;
  height: 2.25rem !important;
  padding: 0 !important;
  border-radius: 0.75rem !important;
}
${glassInside(NAV_ROW, GLASS_NAV_SQUARE_BUTTONS, ['svg'])} {
  width: 1.25rem !important;
  height: 1.25rem !important;
}

/* ── Filled buttons keep their colour, with the same glass finish ─────────────────────────────────────────────────────────────── */
${glassWithin(NAV_ROW, GLASS_NAV_TINTED)},
${SUB_ROW} ${tid('global-search-customer-toggle')}[style*="background-color: rgb(238, 114, 3)"] {
  background: linear-gradient(135deg, rgba(238,114,3,0.94) 0%, rgba(244,163,95,0.84) 100%) !important;
  border: 1px solid rgba(255,255,255,0.45) !important;
  box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12), 0 6px 16px rgba(238,114,3,0.35) !important;
  -webkit-backdrop-filter: blur(14px) saturate(1.6);
  backdrop-filter: blur(14px) saturate(1.6);
}
${glassWithin(NAV_ROW, GLASS_NAV_TINTED, ':hover')} {
  box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(0,0,0,0.12), 0 10px 24px rgba(238,114,3,0.50) !important;
}
${NAV_ROW} ${tid('header-send-answers-btn')} {
  height: 2.25rem !important;
}
${SUB_ROW} ${tid('global-search-project-toggle')}[style*="background-color: rgb(13, 148, 136)"] {
  background: linear-gradient(135deg, rgba(13,148,136,0.95) 0%, rgba(45,212,191,0.85) 100%) !important;
  border: 1px solid rgba(255,255,255,0.45) !important;
  box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12), 0 6px 16px rgba(13,148,136,0.35) !important;
  -webkit-backdrop-filter: blur(14px) saturate(1.6);
  backdrop-filter: blur(14px) saturate(1.6);
}
${NAV_ROW} ${tid('nav-pill-po-expenses')}[style*="147, 51, 234"] {
  background: linear-gradient(135deg, rgba(147,51,234,0.95) 0%, rgba(126,34,206,0.85) 100%) !important;
  border: 1px solid rgba(255,255,255,0.40) !important;
  box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.40), inset 0 -1px 0 rgba(0,0,0,0.12), 0 6px 16px rgba(147,51,234,0.40) !important;
}
${NAV_ROW} ${tid('nav-pill-po-expenses')}[style*="147, 51, 234"] span,
${NAV_ROW} ${tid('nav-pill-po-expenses')}[style*="147, 51, 234"] svg {
  color: #ffffff !important;
}

/* ── Sub-header toggles: lighter accent colours on dark glass ─────────────────────────────────────────────────────────────── */
${SUB_ROW_DARK} ${tid('global-search-customer-toggle')}[style*="background-color: white"] {
  color: var(--g-accent) !important;
}
${SUB_ROW_DARK} ${tid('global-search-project-toggle')}[style*="background-color: white"] {
  color: var(--g-accent-teal) !important;
}

/* ── Search text + icons on dark glass: plain white, no shadow ───────────────────────────────────────────────────────────────── */
${SUB_ROW_DARK} ${tid('global-search-subheader-input')} {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff;
  text-shadow: none !important;
}
${SUB_ROW_DARK} ${tid('global-search-subheader-input')}::placeholder {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff;
  opacity: 1;
  text-shadow: none !important;
}
${SUB_ROW_DARK} ${GLASS_SUB_SEARCH_BAR} svg {
  color: #ffffff !important;
  filter: none !important;
}

/* ── Sub-header / header info chips (Home: day, Stock, INV, Y'day, Sales, Forecast) ───────── */
${SUB_ROW_DARK} .c30-sub-slot > .bg-white [class*="text-black"],
${SUB_ROW_DARK} .c30-sub-slot > .bg-white[class*="text-black"],
${NAV_ROW_DARK} .c30-nav-slot > .bg-white [class*="text-black"],
${NAV_ROW_DARK} .c30-nav-slot > .bg-white[class*="text-black"] {
  color: rgba(255,255,255,0.92) !important;
}
${SUB_ROW_DARK} .c30-sub-slot > .bg-white [class*="text-black/60"],
${NAV_ROW_DARK} .c30-nav-slot > .bg-white [class*="text-black/60"] {
  color: rgba(255,255,255,0.60) !important;
}
/* ── Transport only: the search bar is a pressed-in (inset) field with black text, a black cursor and no white edge ── */
${SUB_ROW}[data-c30-page="transport"] ${GLASS_SUB_SEARCH_BAR} {
  background: linear-gradient(180deg, #eceef1 0%, #f8f9fb 100%) !important;
  border: none !important;
  box-shadow: inset 0 3px 7px rgba(29,29,27,0.30), inset 0 1px 2px rgba(29,29,27,0.20), inset 0 -1px 0 rgba(255,255,255,0.55) !important;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}
${SUB_ROW}[data-c30-page="transport"] ${GLASS_SUB_SEARCH_BAR}.ring-2 {
  box-shadow: inset 0 3px 7px rgba(29,29,27,0.30), 0 0 0 2px rgba(238,114,3,0.75) !important;
}
${SUB_ROW}[data-c30-page="transport"] ${tid('global-search-subheader-input')} {
  color: #000000 !important;
  -webkit-text-fill-color: #000000;
  caret-color: #000000 !important;
  text-shadow: none !important;
}
${SUB_ROW}[data-c30-page="transport"] ${tid('global-search-subheader-input')}::placeholder {
  color: rgba(0,0,0,0.55) !important;
  -webkit-text-fill-color: rgba(0,0,0,0.55);
  opacity: 1;
  text-shadow: none !important;
}
${SUB_ROW}[data-c30-page="transport"] ${GLASS_SUB_SEARCH_BAR} svg {
  color: #000000 !important;
  filter: none !important;
}
/* ── Sub-header Customer / Projects buttons: slightly narrower than before (side padding 0.875rem -> 0.625rem) ── */
${SUB_ROW} ${tid('global-search-customer-toggle')},
${SUB_ROW} ${tid('global-search-project-toggle')} {
  padding-left: 0.625rem !important;
  padding-right: 0.625rem !important;
}
/* ── The search bar is a pressed-in (inset) field on EVERY page, like on Transport. Its edge is a 2px border that is clear
   until a Customer / Projects search is chosen, then takes that button's colour (orange for Customer, teal for Projects). ── */
${SUB_ROW} ${GLASS_SUB_SEARCH_BAR} {
  border: 2px solid transparent !important;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}
${SUB_ROW_LIGHT} ${GLASS_SUB_SEARCH_BAR} {
  background: linear-gradient(180deg, #eceef1 0%, #f8f9fb 100%) !important;
  box-shadow: inset 0 3px 7px rgba(29,29,27,0.30), inset 0 1px 2px rgba(29,29,27,0.20), inset 0 -1px 0 rgba(255,255,255,0.55) !important;
}
${SUB_ROW_DARK} ${GLASS_SUB_SEARCH_BAR} {
  background: linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.22) 100%) !important;
  box-shadow: inset 0 3px 8px rgba(0,0,0,0.60), inset 0 1px 2px rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.14) !important;
}
${SUB_ROW_LIGHT} ${GLASS_SUB_SEARCH_BAR}.ring-2 {
  box-shadow: inset 0 3px 7px rgba(29,29,27,0.30), 0 0 0 2px rgba(238,114,3,0.55) !important;
}
${SUB_ROW_DARK} ${GLASS_SUB_SEARCH_BAR}.ring-2 {
  box-shadow: inset 0 3px 8px rgba(0,0,0,0.60), 0 0 0 2px rgba(255,152,56,0.65) !important;
}
${SUB_ROW_LIGHT} ${tid('global-search-subheader-input')} {
  text-shadow: none !important;
  caret-color: #000000 !important;
}
${SUB_ROW_LIGHT} ${GLASS_SUB_SEARCH_BAR} svg {
  filter: none !important;
}
${SUB_ROW_DARK} ${tid('global-search-subheader-input')} {
  caret-color: #ffffff !important;
}
${SUB_ROW} ${GLASS_SUB_SEARCH_BAR}[data-search-mode="customer"] {
  border: 2px solid var(--g-accent) !important;
}
${SUB_ROW} ${GLASS_SUB_SEARCH_BAR}[data-search-mode="project"] {
  border: 2px solid var(--g-accent-teal) !important;
}
${SUB_ROW} .c30-sub-slot > [style*="rgba(0, 0, 0, 0.22)"] {
  border: 1px solid rgba(255,255,255,0.22) !important;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.28), 0 3px 10px rgba(0,0,0,0.25) !important;
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
}
`;

/**
 * AppHeader — unified global header
 *
 * Desktop structure:
 *   Row 1  white   [Hamburger▼] [pills left] [Transport icon] [Search] [pills right] [Settings⚙]
 *                  Transport icon opens DashboardPanel (dropdown), same content as before —
 *                  now an on-demand icon instead of a permanent bar underneath the header.
 *   Left sidebar — slide-in panel with Quick Links + page nav
 */

// ── Right settings/account menu ───────────────────────────────────────────────────────────────────
export const SettingsMenu = ({
  user, onLogout, navigate, isHomePage,
  canSwitchBranches, accessibleBranches, getEffectiveBranchId, setViewAs, effectiveBranch,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    // The branch <Select> renders its options via a Radix Portal appended to
    // document.body — outside this menu's own DOM subtree. Without this
    // exclusion, clicking a branch option is misread as an "outside click"
    // and closes the whole menu (via setOpen(false)) on mousedown, before the
    // Select's own onValueChange has a chance to fire.
    const onDoc = (e) => {
      if (e.target.closest('.branch-select-portal')) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-orange-50 transition-colors"
        title="Settings & Account"
        data-testid="header-settings-menu-btn"
      >
        <SettingsIcon className="w-6 h-6" style={{ color: enconOrange }} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] w-56 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden py-1"
          style={{ zIndex: 9999 }}
          data-testid="header-settings-dropdown"
        >
          {/* Branch selector */}
          {canSwitchBranches && accessibleBranches.length > 0 && (
            <div className="px-3 py-2.5 border-b border-gray-100">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Branch</span>
              <Select value={getEffectiveBranchId()} onValueChange={(v) => { setViewAs(v); setOpen(false); }}>
                <SelectTrigger
                  className="h-8 rounded-full border-2 font-bold text-sm [&>svg]:hidden"
                  style={{
                    borderColor: getEffectiveBranchId() === 'E70' ? '#8b5cf6' : enconOrange,
                    backgroundColor: getEffectiveBranchId() === 'E70' ? 'rgba(139,92,246,0.12)' : 'rgba(238,114,3,0.12)',
                    color: getEffectiveBranchId() === 'E70' ? '#7c3aed' : enconOrange,
                  }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white branch-select-portal">
                  {accessibleBranches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.code || b.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Static C30 badge when can't switch */}
          {!canSwitchBranches && effectiveBranch && (
            <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Branch</span>
              <span
                className="font-bold text-xs px-2.5 py-1 rounded-full border-2"
                style={{
                  borderColor: effectiveBranch.id === 'E70' ? '#8b5cf6' : enconOrange,
                  color: effectiveBranch.id === 'E70' ? '#8b5cf6' : enconOrange,
                  backgroundColor: effectiveBranch.id === 'E70' ? 'rgba(139,92,246,0.1)' : 'rgba(238,114,3,0.1)',
                }}
              >
                {effectiveBranch.code || effectiveBranch.id}
              </span>
            </div>
          )}

          {/* Settings */}
          {(user?.role === 'super_admin' || user?.can_access_settings) && (
            <button
              onClick={() => { navigate('/settings'); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
              data-testid="settings-menu-settings-btn"
            >
              <SettingsIcon className="w-4 h-4" style={{ color: enconOrange }} />
              <span className="font-medium text-gray-700">Settings</span>
            </button>
          )}

          {/* Home (only off home) */}
          {!isHomePage && (
            <button
              onClick={() => { navigate('/'); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
              data-testid="settings-menu-home-btn"
            >
              <Home className="w-4 h-4" style={{ color: enconOrange }} />
              <span className="font-medium text-gray-700">Home</span>
            </button>
          )}

          <div className="my-1 border-t border-gray-100" />

          {/* Logout */}
          <button
            onClick={() => { onLogout?.(); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-red-50 transition-colors"
            data-testid="settings-menu-logout-btn"
          >
            <LogOut className="w-4 h-4 text-red-500" />
            <span className="font-medium text-red-600">Log Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ── Left sidebar ──────────────────────────────────────────────────────────────────────────
// Glass finish for the sidebar (hamburger menu) — the same frosted look as the Home page glass cards and
// the header buttons: smoked graphite glass on dark, white frosted glass on light. It is a full-height
// panel that sits ABOVE the header (top of the screen to the bottom) with only its top-right corner
// rounded and a straight bottom edge. It is a large panel over the page content, so it is denser than
// the small header buttons to keep everything on it readable.
const SIDEBAR_GLASS_STYLE = {
  dark: {
    background: 'linear-gradient(160deg, rgba(62,64,73,0.82) 0%, rgba(32,33,39,0.90) 55%, rgba(24,25,29,0.94) 100%)',
    backdropFilter: 'blur(24px) saturate(1.5)',
    WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
    borderRight: '1px solid rgba(255,255,255,0.18)',
    // light default text, so anything that does not set its own colour (or a browser without color-mix) stays readable
    color: 'rgba(255,255,255,0.92)',
  },
  light: {
    background: 'linear-gradient(160deg, rgba(255,255,255,0.90) 0%, rgba(255,247,240,0.84) 55%, rgba(255,255,255,0.92) 100%)',
    backdropFilter: 'blur(24px) saturate(1.6)',
    WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
    borderRight: '1px solid rgba(238,114,3,0.28)',
    color: '#1d1d1b',
  },
};
// The outer shadow only shows while the panel is open, so it never bleeds onto the screen edge when closed.
const SIDEBAR_GLASS_SHADOW = {
  dark: {
    open: 'inset 0 1.5px 0 rgba(255,255,255,0.22), inset -1px 0 0 rgba(255,255,255,0.06), 14px 0 44px rgba(0,0,0,0.55)',
    closed: 'inset 0 1.5px 0 rgba(255,255,255,0.22), inset -1px 0 0 rgba(255,255,255,0.06)',
  },
  light: {
    open: 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset -1px 0 0 rgba(238,114,3,0.10), 14px 0 44px rgba(29,29,27,0.20)',
    closed: 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset -1px 0 0 rgba(238,114,3,0.10)',
  },
};
// Lifts a colour towards white so category / app colours (some of them dark) stay readable on dark glass.
const sidebarOnDark = (color, fallback) => `color-mix(in srgb, ${color || fallback} 68%, white)`;

const LeftSidebar = ({ isOpen, onClose, user, navigate, glassDark = false }) => {
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  const [hubApps, setHubApps] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    const hasAccess = (permKey) => !permKey || user?.role === 'super_admin' || user?.[permKey];
    (async () => {
      try {
        const [linksRes, groupsRes, appsRes] = await Promise.all([
          api.get(`${base}/api/quick-links`),
          api.get(`${base}/api/quick-link-groups`),
          api.get(`${base}/api/hub-apps`),
        ]);
        if (cancelled) return;
        setQuickLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
        setLinkGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
        setHubApps(
          (Array.isArray(appsRes.data) ? appsRes.data : []).filter(app => hasAccess(app.permission_key))
        );
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [isOpen, user?.id]); // eslint-disable-line

  const getLinksForGroup = (gId) =>
    quickLinks.filter(l => (l.group_id || 'default') === gId).sort((a, b) => (a.order || 0) - (b.order || 0));
  const nonEmpty = linkGroups.filter(g => getLinksForGroup(g.id).length > 0);

  // Lucide icon map matching Hub.jsx iconMap
  const iconMap = {
    Truck, FileText, Car: Package, Settings: SettingsIcon, TrendingUp, ClipboardList, Package,
    BarChart2, PackageCheck, ExternalLink, Link2,
  };
  const getIcon = (iconType) => iconType && iconMap[iconType] ? iconMap[iconType] : Package;

  const D = glassDark;
  const theme = D ? 'dark' : 'light';
  // Glass tile used for the Navigation items and the close button.
  const glassTileClass = D
    ? 'bg-white/[0.06] hover:bg-white/[0.13] border border-white/[0.12] hover:border-white/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.16)]'
    : 'bg-white/70 hover:bg-white border border-orange-200/60 hover:border-orange-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_2px_8px_rgba(29,29,27,0.06)]';
  const navItemClass = `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors group ${glassTileClass}`;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className={`fixed inset-0 z-[98] backdrop-blur-[2px] ${D ? 'bg-black/40' : 'bg-black/25'}`}
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar panel — full height, above the header; only the top-right corner is rounded */}
      <div
        className={`fixed left-0 z-[99] rounded-tr-3xl overflow-hidden transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          top: 0, bottom: 0, width: '270px',
          ...SIDEBAR_GLASS_STYLE[theme],
          boxShadow: isOpen ? SIDEBAR_GLASS_SHADOW[theme].open : SIDEBAR_GLASS_SHADOW[theme].closed,
        }}
        data-testid="left-sidebar-panel"
        data-c30-sidebar-glass={theme}
      >
        <div className="overflow-y-auto h-full">

          {/* Top row — the panel now covers the header's hamburger button, so it has its own close button */}
          <div className="flex items-center justify-between px-4 pt-4 pb-1">
            <span className={`text-[11px] font-bold uppercase tracking-[0.18em] ${D ? 'text-white/50' : 'text-gray-400'}`}>Menu</span>
            <button
              type="button"
              onClick={onClose}
              title="Close menu"
              className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${glassTileClass}`}
              data-testid="sidebar-close-btn"
            >
              <X className="w-5 h-5" style={{ color: D ? '#ff9838' : enconOrange }} />
            </button>
          </div>

          {/* Quick Links section */}
          {nonEmpty.length > 0 && (
            <div className={`px-4 pt-4 pb-3 border-b ${D ? 'border-white/10' : 'border-orange-100'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4" style={{ color: D ? '#c4b5fd' : '#6d28d9' }} />
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: D ? '#c4b5fd' : '#6d28d9' }}>Quick Links</span>
              </div>
              {nonEmpty.map((group, gIdx) => {
                const groupLinks = getLinksForGroup(group.id);
                return (
                  <div key={group.id} className={gIdx > 0 ? 'mt-3' : ''}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: D ? sidebarOnDark(group.color, '#a78bfa') : (group.color || '#6d28d9') }}>
                      {group.name}
                    </p>
                    <div className="space-y-0.5">
                      {groupLinks.map(link => (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onClose}
                          className={`flex items-center gap-2 px-2 py-2 rounded-lg transition-colors group ${D ? 'hover:bg-white/10' : 'hover:bg-white/80'}`}
                          data-testid={`sidebar-quick-link-${link.id}`}
                        >
                          <ExternalLink className={`w-3 h-3 shrink-0 ${D ? 'text-white/50 group-hover:text-white' : 'text-gray-400 group-hover:text-purple-600'}`} />
                          <span className="text-sm font-medium flex-1 truncate" style={{ color: D ? 'rgba(255,255,255,0.92)' : (group.color || '#6d28d9') }}>
                            {link.name}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Navigation section — mirrors Hub page cards in order */}
          {hubApps.length > 0 && (
            <div className="px-4 pt-4 pb-8">
              <div className="flex items-center gap-2 mb-3">
                <Menu className="w-4 h-4" style={{ color: D ? '#ff9838' : enconOrange }} />
                <span className={`text-xs font-bold uppercase tracking-wider ${D ? 'text-white/50' : 'text-gray-500'}`}>Navigation</span>
              </div>
              <div className="space-y-1">
                {hubApps.map(app => {
                  const Icon = app.icon_type ? getIcon(app.icon_type) : Package;
                  const label = app.name || app.title;
                  const route = app.route;
                  const iconColor = D ? sidebarOnDark(app.color, '#ff9838') : (app.color || enconOrange);
                  const labelClass = `font-medium text-sm ${D ? 'text-white/90 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`;
                  return app.is_external ? (
                    <a
                      key={app.id}
                      href={route}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onClose}
                      className={navItemClass}
                      data-testid={`sidebar-nav-${app.id}`}
                    >
                      <Icon className="w-4 h-4 shrink-0 group-hover:scale-110 transition-transform" style={{ color: iconColor }} />
                      <span className={labelClass}>{label}</span>
                      <ExternalLink className={`w-3 h-3 ml-auto ${D ? 'text-white/40' : 'text-gray-400'}`} />
                    </a>
                  ) : (
                    <button
                      key={app.id}
                      onClick={() => { navigate(route); onClose(); }}
                      className={`${navItemClass} text-left`}
                      data-testid={`sidebar-nav-${app.id}`}
                    >
                      <Icon className="w-4 h-4 shrink-0 group-hover:scale-110 transition-transform" style={{ color: iconColor }} />
                      <span className={labelClass}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── Main AppHeader ─────────────────────────────────────────────────────────────────
const AppHeader = ({
  user,
  onLogout,
  rightButtons = null,
  leftButtons = null,
  mobileMenuButtons = [],
  scrollWithPage = false,
  subHeaderLeft = null,
  subHeaderCenter = null,
  subHeaderCenterLeft = null,
  subHeaderCenterRight = null,
  subHeaderRight = null,
  hideGlobalNotificationBar = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarTop, setSidebarTop] = useState(84);
  const [subHeaderBottom, setSubHeaderBottom] = useState(84);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [orgDefaultHeaderStyle, setOrgDefaultHeaderStyle] = useState(null);
  const headerRef = useRef(null);
  const subHeaderEndRef = useRef(null);
  // Invisible viewport-sized box used to measure the page zoom (see utils/modalViewport.js).
  const viewportProbeRef = useRef(null);
  const { enabled: headerDotsFlagEnabled } = useFeatureFlag(HEADER_DOTS_FLAG);

  // Org-wide default Global Sub-Header style (fetched once) — falls back to
  // this when the current user has no personal preference set.
  useEffect(() => {
    api.get(`${API}/settings/header-style-default`)
      .then(r => setOrgDefaultHeaderStyle(r.data))
      .catch(() => {});
  }, []);

  // Poll for pending "Send for review" (Order Book) requests addressed to me.
  useEffect(() => {
    let cancelled = false;
    const loadPending = () => {
      api.get(`${API}/order-book-reports/review-requests/pending`)
        .then(r => { if (!cancelled) setPendingReviewCount((r.data?.requests || []).length); })
        .catch(() => {});
    };
    loadPending();
    const interval = setInterval(loadPending, 45000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // If "Send Answers" was clicked from another page, we navigate home first,
  // then open the modal here once we land on '/'.
  useEffect(() => {
    if (location.pathname === '/' && sessionStorage.getItem('open_review_answers_modal') === '1') {
      sessionStorage.removeItem('open_review_answers_modal');
      setShowAnswerModal(true);
    }
  }, [location.pathname]);

  const openAnswersModal = () => {
    sessionStorage.setItem('open_review_answers_modal', '1');
    if (location.pathname === '/') {
      sessionStorage.removeItem('open_review_answers_modal');
      setShowAnswerModal(true);
    } else {
      navigate('/');
    }
  };

  const closeAnswersModal = () => {
    setShowAnswerModal(false);
    api.get(`${API}/order-book-reports/review-requests/pending`)
      .then(r => setPendingReviewCount((r.data?.requests || []).length))
      .catch(() => {});
  };

  // Measure header height so sidebar top aligns exactly with transport bar bottom.
  // ResizeObserver (not just window resize) so the measurement also reacts to the
  // header's OWN content height changing — e.g. the Activity Bar's tab row
  // appearing/disappearing as activities are opened/closed. `subHeaderBottom` is
  // measured separately (a zero-height marker right after the sub-header, before
  // the Activity Bar) — it stays constant regardless of the Activity Bar's own
  // height, so modals can be positioned right under the sub-header only.
  useEffect(() => {
    const measure = () => {
      if (headerRef.current) setSidebarTop(headerRef.current.offsetHeight);
      if (subHeaderEndRef.current) {
        // Zoom-proof: on laptops / iPads in landscape the page is zoomed to 0.8, and getBoundingClientRect() reports
        // on-screen pixels while a modal's `top` is scaled by that zoom again. `bottom` is therefore converted to the
        // page's own CSS pixels (using the viewport probe to measure the real zoom), and the viewport height in
        // those same pixels is published as --c30-vh for the modals' height cap. See utils/modalViewport.js.
        const m = computeModalViewport(viewportProbeRef.current, subHeaderEndRef.current);
        setSubHeaderBottom(m.bottom);
        applyModalViewportVars(m);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    const ro = new ResizeObserver(measure);
    if (headerRef.current) ro.observe(headerRef.current);
    // The probe is exactly the viewport, so this also fires when the window is resized / rotated AND when the page
    // zoom is applied or removed (which changes the viewport's size in the page's own pixels but fires no resize).
    if (viewportProbeRef.current) ro.observe(viewportProbeRef.current);
    // Modal panels (the dimmed overlay's one child, capped with calc(100vh - ...)) are tagged so the safety-net
    // style below can make them fill the overlay, i.e. run from the sub-header to just above the bottom of the screen.
    const stopWatchingModalPanels = watchModalPanels();
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      ro.disconnect();
      stopWatchingModalPanels();
    };
  }, []);

  const { modalUiV2, navUiV2 } = useUIPreview();
  const { setHeaderHeight, activeActivity, barSuppressed } = useActivityBar();
  // Modal UI v2 positions modals right below the sub-header (excluding the
  // Activity Bar's own height) so a modal can visually extend over the tabs.
  useEffect(() => { setHeaderHeight(subHeaderBottom); }, [subHeaderBottom, setHeaderHeight]);

  const {
    getEffectiveBranchId,
    setViewAs,
    canSwitchBranches,
    getAccessibleBranches,
    getEffectiveBranch,
  } = useBranch();

  const accessibleBranches = getAccessibleBranches();
  const effectiveBranch    = getEffectiveBranch();
  const isHomePage = location.pathname === '/' || location.pathname === '';
  // Home page background theme is a personal per-user preference (Settings > General), not an admin feature flag.
  const homeDarkGlassEnabled = user?.home_theme === 'dark';
  // Transparent (not a duplicated gradient) — AppHeader is rendered INSIDE
  // Hub.jsx's own dark-gradient page background, so making this bar
  // transparent lets that actual background paint straight through behind
  // it, guaranteeing a pixel-perfect match instead of two separate gradients
  // that would never quite line up.
  // Global Sub-Header background style — resolved in priority order:
  // 1) this user's own personal choice (Settings > General > My Personal Style,
  //    tri-state — null means "not set"), 2) the org-wide default (Settings >
  //    General > Default Style For All Users, super_admin managed), 3) the
  //    original standard orange/white/orange gradient.
  let subHeaderBackground = subHeaderOrangeGradient;
  if (user?.subheader_dark_gradient === true) {
    subHeaderBackground = subHeaderDarkGradient;
  } else if (user?.subheader_orange_bar === true) {
    subHeaderBackground = subHeaderOrangeBarGradient;
  } else if (user?.subheader_orange_white === true) {
    subHeaderBackground = subHeaderOrangeWhiteGradient;
  } else if (user?.subheader_dark_gradient == null && user?.subheader_orange_bar == null && user?.subheader_orange_white == null) {
    if (orgDefaultHeaderStyle?.default_subheader_dark_gradient) {
      subHeaderBackground = subHeaderDarkGradient;
    } else if (orgDefaultHeaderStyle?.default_subheader_orange_bar) {
      subHeaderBackground = subHeaderOrangeBarGradient;
    } else if (orgDefaultHeaderStyle?.default_subheader_orange_white) {
      subHeaderBackground = subHeaderOrangeWhiteGradient;
    }
  }
  const gifUrl = '/dashboard-logo-transparent.gif';
  // Global Header background style — same priority-order resolution as the sub-header,
  // reusing the Home page's own dark palette for visual consistency when active.
  const resolvedHeaderDarkGradient = user?.header_dark_gradient ?? orgDefaultHeaderStyle?.default_header_dark_gradient ?? false;
  // Dotted-graphite header needs BOTH the dark Home theme and its own Manage Changes toggle;
  // while active it takes priority over the plain dark / white header styles.
  const headerDotsActive = headerDotsFlagEnabled && homeDarkGlassEnabled;
  // Light Home theme + the same toggle: the white dotted header (unless the user picked the dark header themselves).
  const headerDotsLightActive = headerDotsFlagEnabled && !homeDarkGlassEnabled && !resolvedHeaderDarkGradient;
  const headerBackground = headerDotsActive
    ? headerDotsGradient
    : (headerDotsLightActive
      ? headerDotsLightGradient
      : (resolvedHeaderDarkGradient ? headerDarkGradient : '#ffffff'));
  const positionClass = scrollWithPage ? '' : 'fixed top-0 left-0 right-0 z-50';
  // Glass theme per row (see HEADER_GLASS_CSS): each row's glass follows what it is sitting on.
  // The nav row is dark when it shows the dotted / dark header, otherwise it is the white bar.
  // The sub-header is dark on the dark Home screen, on Transport, and with the dark sub-header style.
  const onTransportPage = location.pathname.startsWith('/transport');
  const navGlassTheme = (headerDotsActive || resolvedHeaderDarkGradient) ? 'dark' : 'light';
  // The sidebar (hamburger menu) is dark glass whenever the header itself is dark glass, and on the dark Home screen;
  // otherwise it is white glass. It is full height above the header, so it does not need the header's height.
  const sidebarGlassDark = navGlassTheme === 'dark' || (isHomePage && homeDarkGlassEnabled);
  // Light mode, every page except Home (Home draws its own copy just below): the same white waves background
  // on every page that had a plain white / grey background. See LightPageBackground.jsx.
  const lightPageBackgroundActive = !isHomePage && !homeDarkGlassEnabled;
  const subHeaderDarkStyle = subHeaderBackground === subHeaderDarkGradient;
  const subGlassTheme = (subHeaderDarkStyle || (isHomePage && homeDarkGlassEnabled)) ? 'dark' : 'light';
  // The desktop sub-header's dark style now uses the SAME transparent gradient as the default bar — only
  // its buttons go dark (dark glass, see subGlassTheme). The old solid dark middle is kept for the mobile bar.
  const desktopSubHeaderBackgroundBase = subHeaderDarkStyle ? subHeaderOrangeGradient : subHeaderBackground;
  // On Transport the default bar fades from orange to WHITE (not to see-through, which showed the dark map behind it).
  const desktopSubHeaderBackground = (onTransportPage && desktopSubHeaderBackgroundBase === subHeaderOrangeGradient)
    ? subHeaderOrangeWhiteGradient
    : desktopSubHeaderBackgroundBase;
  // Dark buttons sitting on a light page need denser glass (see HEADER_GLASS_CSS).
  const subGlassSolid = subGlassTheme === 'dark' && !(isHomePage && homeDarkGlassEnabled) && !onTransportPage;

  // Limited Access UI: pages still render <AppHeader> exactly as before, but inside LimitedShell the full
  // header is replaced by the limited top bar, and the page's own sub-header content moves into that bar.
  const limitedShell = useLimitedShell();
  // UI V3: when the page is being drawn inside the V3 shell (only while Settings > Dashboard V3 has V3 on for this person), the
  // full header is replaced by the V3 page control bar. The page's own header slots (leftButtons, rightButtons and the five
  // subHeader slots) are handed straight to it, so every control the page has keeps working exactly as before.
  const v3Shell = useV3Shell();
  if (v3Shell && !limitedShell) {
    return (
      <>
        <div ref={viewportProbeRef} aria-hidden="true" data-testid="c30-viewport-probe" style={VIEWPORT_PROBE_STYLE} />
        <V3PageToolbar
          left={[leftButtons, subHeaderLeft]}
          center={[subHeaderCenterLeft ?? subHeaderCenter, subHeaderCenterRight]}
          right={[
            pendingReviewCount > 0 ? (
              <button
                key="v3-send-answers"
                onClick={openAnswersModal}
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:brightness-105 transition-all shrink-0"
                style={{ backgroundColor: enconOrange }}
                title="You have pending Order Book review requests"
                data-testid="header-send-answers-btn"
              >
                <Send className="w-3.5 h-3.5" /> Send Answers ({pendingReviewCount})
              </button>
            ) : null,
            subHeaderRight,
            rightButtons,
          ]}
        />
        <div ref={subHeaderEndRef} />
        {!hideGlobalNotificationBar && <GlobalNotificationBar user={user} />}
        <OrderBookAnswerModal isOpen={showAnswerModal} onClose={closeAnswersModal} />
        <style>{MODAL_VIEWPORT_CSS}</style>
      </>
    );
  }
  if (limitedShell) {
    return (
      <>
        <LimitedTopBar
          user={user}
          left={<>{leftButtons}{subHeaderLeft}</>}
          center={<>{subHeaderCenterLeft ?? subHeaderCenter}{subHeaderCenterRight}</>}
          right={<>{subHeaderRight}{rightButtons}</>}
        />
        {!hideGlobalNotificationBar && <GlobalNotificationBar user={user} />}
      </>
    );
  }

  return (
    <>
      {/* Invisible, zero-width box pinned to exactly the viewport. Comparing how tall the browser reports it on screen
          with its own CSS-pixel height reveals the page zoom, so modals can start exactly under the sub-header and
          finish just above the bottom of the screen at any zoom / screen size (see utils/modalViewport.js). */}
      <div ref={viewportProbeRef} aria-hidden="true" data-testid="c30-viewport-probe" style={VIEWPORT_PROBE_STYLE} />

      {/* Home screen in LIGHT mode: the white counterpart of the dark Home background (soft white gradient with
          the same contour waves). The dark version is rendered by Hub.jsx; this one is rendered here so the
          Hub page file does not need to change. Fixed layers pinned to the viewport, behind the page content. */}
      {isHomePage && !homeDarkGlassEnabled && <HubLightBackground />}

      {/* Every other page in LIGHT mode: the same background behind the page, and the page's plain white / grey
          wrapper made see-through so it shows (pages with a dark, coloured or image background are left alone). */}
      <LightPageBackground startRef={headerRef} active={lightPageBackgroundActive} routeKey={location.pathname} />

      {/* ══════════════════════════════════════════════════════════════════════════
          LEFT SIDEBAR (desktop only, slide-in from left)
      ══════════════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <LeftSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          user={user}
          navigate={navigate}
          glassDark={sidebarGlassDark}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP HEADER  (hidden on mobile)
      ══════════════════════════════════════════════════════════════════ */}
      <div
        ref={headerRef}
        className={`hidden md:block ${positionClass}`}
      >
        {/* ── White nav row — grid centres pills at true screen centre, no overlapping layers ── */}
        <div
          className="px-3 lg:px-4 py-3.5"
          data-testid="global-header-nav-row"
          data-c30-glass={navGlassTheme}
          style={{
            position: 'relative',
            zIndex: 40,
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            background: headerBackground,
          }}
        >

          {/* Left cell: sidebar toggle + page-specific left buttons */}
          <div className="c30-nav-slot flex items-center gap-1.5 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(o => !o)}
              className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors shrink-0 ${isSidebarOpen ? 'bg-orange-100' : 'hover:bg-orange-50'}`}
              title="Quick Links & Navigation"
              data-testid="header-sidebar-toggle-btn"
            >
              {isSidebarOpen
                ? <X className="w-6 h-6" style={{ color: enconOrange }} />
                : <Menu className="w-6 h-6" style={{ color: enconOrange }} />}
            </button>
            {leftButtons}
          </div>

          {/* Centre cell: M45NavPills — auto-sized so its midpoint equals screen centre.
              Nav UI v2 reserves a fixed-width slot for the sector links internally
              (see M45NavPills navUiV2 branch) so the whole block's total width — and
              therefore the Search/Sector/Delivery/Call anchors either side of it —
              never changes when switching sectors. */}
          <div className="flex items-center justify-center">
            <M45NavPills
              user={user}
              inHeader={true}
              isTransportOpen={isDashboardOpen}
              onToggleTransport={() => setIsDashboardOpen((o) => !o)}
              navUiV2={navUiV2}
            />
          </div>

          {/* Center cell continued: Transport icon rendered inside M45NavPills, immediately left of Search */}

          {/* Right cell: page-specific buttons + settings + home */}
          <div className="c30-nav-slot flex items-center gap-1.5 justify-end min-w-0">
            {pendingReviewCount > 0 && (
              <button
                onClick={openAnswersModal}
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:brightness-105 transition-all shrink-0 animate-pulse"
                style={{ backgroundColor: enconOrange }}
                title="You have pending Order Book review requests"
                data-testid="header-send-answers-btn"
              >
                <Send className="w-3.5 h-3.5" /> Send Answers ({pendingReviewCount})
              </button>
            )}
            {rightButtons}
            <SettingsMenu
              user={user}
              onLogout={onLogout}
              navigate={navigate}
              isHomePage={isHomePage}
              canSwitchBranches={canSwitchBranches}
              accessibleBranches={accessibleBranches}
              getEffectiveBranchId={getEffectiveBranchId}
              setViewAs={setViewAs}
              effectiveBranch={effectiveBranch}
            />
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-orange-50 transition-colors shrink-0"
              title="Go to Home"
              data-testid="header-home-btn"
            >
              <Home className="w-6 h-6" style={{ color: enconOrange }} />
            </button>
          </div>
        </div>

        {/* ── Global Sub-Header row — PERMANENT structural second row of the SAME
             fixed header (not a floating bar in page content), flush under the nav
             row with zero gap, on every single page now (not just Transport). Solid
             opaque orange→white→orange gradient (no glass/blur/transparency) with
             rounded bottom corners, floating over the page. Slots are optional —
             pages with nothing to show yet just render the empty gradient bar so the
             app-wide look stays consistent. ── */}
        <div
          className="w-full h-10 grid items-center gap-2 px-4 rounded-b-3xl"
          style={{
            position: 'relative',
            zIndex: 39,
            background: desktopSubHeaderBackground,
            gridTemplateColumns: '1fr minmax(0,1fr) auto minmax(0,1fr) 1fr',
          }}
          data-testid="global-sub-header"
          data-c30-glass={subGlassTheme}
          data-c30-page={onTransportPage ? 'transport' : undefined}
          data-c30-glass-solid={subGlassSolid ? 'true' : undefined}
        >
          <div className="c30-sub-slot flex items-center gap-1.5 justify-self-start min-w-0">{subHeaderLeft}</div>
          <div className="c30-sub-slot flex items-center gap-2 justify-self-end min-w-0 overflow-hidden">{subHeaderCenterLeft ?? subHeaderCenter}</div>
          <div className="flex items-center justify-self-center">
            <GlobalSearch user={user} dropdownTop={sidebarTop} />
          </div>
          <div className="c30-sub-slot flex items-center gap-2 justify-self-start min-w-0 overflow-hidden">{subHeaderCenterRight}</div>
          <div className="c30-sub-slot flex items-center gap-2 justify-self-end min-w-0">{subHeaderRight}</div>
        </div>

        {/* Zero-height marker — bottom edge = where the sub-header visually ends,
            used to position modals so they start there regardless of the Activity
            Bar's own height (see subHeaderBottom above). */}
        <div ref={subHeaderEndRef} />

        {/* ── Activity Bar — Modal UI v2 only. Stays in normal document flow (pushes
             page content down, on every page, never overlays it) directly under the
             sub-header. Dims to look like part of the background whenever a modal
             from this system is open on top of it. ── */}
        {/* The Open / Recent tabs are not shown on Transport at all: the board (or the Unplanned Queue in edit mode) sits
             directly under the sub-header there. They are unchanged on every other page. */}
        {modalUiV2 && !onTransportPage && <ActivityBar dimmed={!!activeActivity || barSuppressed} glassTheme={subGlassTheme} glassSolid={subGlassSolid} />}

      </div>

      {/* DashboardPanel — rendered OUTSIDE the z-50 header so z-index stack is clean */}
      <DashboardPanel
        isOpen={isDashboardOpen}
        user={user}
        onClose={() => setIsDashboardOpen(false)}
      />

      {/* ── Global Notification Bar — SCROLLS with page, positioned below fixed header.
           position:relative + z-index so it still paints above a full-bleed page
           background (e.g. Transport's fixed map layer) instead of being hidden behind it.
           Content itself can be suppressed per-page (hideGlobalNotificationBar) while still
           keeping the spacer div so layout below always sits at the correct offset. ── */}
      <div
        className={`hidden md:block ${(isHomePage || lightPageBackgroundActive) ? '' : 'bg-gray-100'}`}
        style={{ marginTop: scrollWithPage ? 0 : `${sidebarTop}px`, position: 'relative', zIndex: 35 }}
      >
        {!hideGlobalNotificationBar && <GlobalNotificationBar user={user} />}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MOBILE HEADER  (single row + hamburger, unchanged)
      ══════════════════════════════════════════════════════════════════ */}
      <div
        className={`md:hidden ${positionClass}`}
        style={{ borderBottom: `2px solid ${enconBlack}` }}
      >
        <div className="px-2 py-2 flex items-center justify-between gap-1" data-testid="global-header-nav-row-mobile" style={{ background: headerBackground }}>
          {/* Left: logo (opens dashboard) */}
          <button
            onClick={() => setIsDashboardOpen(o => !o)}
            className={`flex items-center gap-0.5 p-1 rounded-lg flex-shrink-0 ${isDashboardOpen ? 'bg-orange-50' : ''}`}
          >
            <img src={gifUrl} alt="Dashboard" className="w-9 h-9" style={{ objectFit: 'contain' }} />
            {isDashboardOpen
              ? <ChevronUp className="w-3.5 h-3.5" style={{ color: enconOrange }} />
              : <ChevronDown className="w-3.5 h-3.5" style={{ color: enconOrange }} />}
          </button>

          {/* Center: search */}
          <GlobalSearch isMobile={true} user={user} />

          {/* Right: back + hamburger */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {pendingReviewCount > 0 && (
              <button
                onClick={openAnswersModal}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg"
                style={{ backgroundColor: enconOrange, color: 'white' }}
                title="Pending Order Book review requests"
                data-testid="header-send-answers-btn-mobile"
              >
                <Send className="w-4.5 h-4.5" />
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{pendingReviewCount}</span>
              </button>
            )}
            {!isHomePage && (
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-100"
                style={{ color: enconOrange }}
              >
                <Home className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="w-9 h-9 flex items-center justify-center rounded-lg"
              style={{ color: (resolvedHeaderDarkGradient || headerDotsActive) ? '#ffffff' : enconBlack }}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* ── Mobile Global Sub-Header — same permanent gradient bar as desktop,
             condensed onto one wrapping row since mobile has no 3-column space.
             Always renders (even empty) to match the desktop bar everywhere. ── */}
        <div
          className="w-full min-h-[36px] flex items-center flex-wrap gap-1.5 px-3 py-1.5 rounded-b-3xl"
          style={{
            background: subHeaderBackground,
          }}
          data-testid="global-sub-header-mobile"
        >
          {subHeaderLeft}
          {subHeaderCenter}
          {subHeaderRight}
        </div>

        {/* Mobile DashboardPanel */}
        <DashboardPanel
          isOpen={isDashboardOpen}
          user={user}
          onClose={() => setIsDashboardOpen(false)}
        />
      </div>

      {/* Mobile hamburger drawer */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/50"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-[280px] bg-white shadow-xl animate-slideLeft flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ backgroundColor: enconOrange }}>
              <span className="text-white font-bold">Menu</span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-1 rounded-full hover:bg-white/20">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(user?.role === 'super_admin' || user?.can_access_settings) && (
                <button
                  onClick={() => { navigate('/settings'); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                >
                  <SettingsIcon className="w-5 h-5" style={{ color: enconOrange }} />
                  <span className="font-medium">Settings</span>
                </button>
              )}
              {mobileMenuButtons && mobileMenuButtons.length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <span className="text-xs text-gray-500 font-medium px-1">Page Actions</span>
                  <div className="flex flex-col gap-2">
                    {mobileMenuButtons.map((btn, i) => {
                      const Icon = btn.icon;
                      return (
                        <button
                          key={i}
                          onClick={() => { btn.onClick?.(); setIsMobileMenuOpen(false); }}
                          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                          style={btn.style || {}}
                        >
                          {Icon && <Icon className="w-5 h-5" style={{ color: btn.color || enconOrange }} />}
                          <span className="font-medium" style={{ color: btn.textColor || '#374151' }}>{btn.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="pt-3 border-t">
                <M45NavPills user={user} mobileMenu={true} />
              </div>
              <div className="pt-4 border-t">
                {isHomePage ? (
                  <button
                    onClick={() => { onLogout?.(); setIsMobileMenuOpen(false); }}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl"
                    style={{ backgroundColor: enconOrange, color: 'white' }}
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-semibold">Logout</span>
                  </button>
                ) : (
                  <button
                    onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl"
                    style={{ backgroundColor: enconOrange, color: 'white' }}
                  >
                    <Home className="w-5 h-5" />
                    <span className="font-semibold">HOME</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <OrderBookAnswerModal isOpen={showAnswerModal} onClose={closeAnswersModal} />

      {/* Glass styling for every Global Header / Sub-Header button (see HEADER_GLASS_CSS at the top of
          this file). It only touches the header controls themselves, inside the two rows' own
          data-c30-glass attribute, so dropdown menus, modals and the mobile header are unaffected.
          It also includes the earlier dark-Home search-text rule (plain white, no shadow), which now
          applies wherever the sub-header is dark. */}
      <style>{HEADER_GLASS_CSS}</style>

      {/* Modal UI v2: modal panels tagged by watchModalPanels() fill their dimmed overlay (sub-header to just above
          the bottom of the screen) at any zoom, instead of using a 100vh cap (see utils/modalViewport.js). */}
      <style>{MODAL_VIEWPORT_CSS}</style>

      {/* Home screen: the notification cards row (Stock, Tasks/Sched Calls, Quotes/Orders, PI Counts,
          8x8) stays on ONE line at desktop / tablet-landscape widths, sized exactly like the main
          tile cards below it. Each card is sized as a fifth of the row minus its share of the gaps,
          which leaves zero spare room, so tiny sub-pixel rounding (especially with the page zoom
          on laptops and iPads) could push the last card onto a second line. Not wrapping — and
          letting the cards give up that fraction of a pixel — keeps all five on one line.
          Uses the same 1024px breakpoint as the tile grid (Tailwind's lg). */}
      {isHomePage && (
        <style>{`
          @media (min-width: 1024px) {
            [data-testid="notification-cards-row"] {
              flex-wrap: nowrap !important;
            }
            [data-testid="notification-cards-row"] > div {
              flex-shrink: 1 !important;
              min-width: 0 !important;
            }
          }
        `}</style>
      )}

      <style>{`
        @keyframes slideLeft {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        .animate-slideLeft { animation: slideLeft 0.25s ease-out forwards; }
      `}</style>
    </>
  );
};

export default AppHeader;
